import NavigableValidation from "./navigableValidation";
import ModelMapping from "~/models/mapping/modelMapping";
import FieldPresenceValidator from "~/models/navigableValidators/fieldPresenceValidator";
import EmbeddedCollectionValidator from "~/models/navigableValidators/embeddedCollectionValidator";
import {TValidator} from "~/interfaces/validationInterfaces";
import NavigableValidator, {INavigableValidatorOptions} from "~/models/navigableValidators/navigableValidator";
import {ErrorType} from "~/enums/ErrorType";
import {Hash} from "~/interfaces/hashInterfaces";
import {ValidationModalSection} from "~/models/navigableValidations/validationSections";
import scrollToElement from "~/helpers/scrollToElement";
import {CommonComponents} from "~/commonSelectors";
import {
  ModelMappingUpdateRules,
  ModelMappingCantAddValues
} from "../../../formability/mappingBuilder/enums/model-mapping-rules";
import ModelMappingOriginLookupField from "../mapping/modelMappingOriginLookupField";
import ModelMappingOriginRelationIdentifier from "../mapping/modelMappingOriginRelationIdentifier";
import ModelMappingDefinition from "~/models/mapping/modelMappingDefinition";

export default class ModelMappingValidation<T> extends NavigableValidation<ModelMapping> {
  validators = [
    new NavigableValidator(this.atLeastOneMappingValueOpts),
    new NavigableValidator(this.atleastOneLookupKeyOpts),
    new NavigableValidator(this.hasValidRelation),
    new EmbeddedCollectionValidator({
      validatorKey: "model_mapping_values",
      section: ValidationModalSection.Layout
    }, this.model),
    ...Object.keys(this.presenceValidatableFields).map<TValidator<string>>(fieldKey => {
      return new FieldPresenceValidator({
        validatorKey: fieldKey,
        section: ValidationModalSection.Layout,
        ...Object.assign({}, this.presenceValidatableFields[fieldKey])
      })
    }),
    new EmbeddedCollectionValidator({
      validatorKey: 'model_mappings',
      section: ValidationModalSection.Mapping,
    }, this.model) // validates nested model mappings
  ]

  // Validators Options
  get presenceValidatableFields(): Hash<Partial<INavigableValidatorOptions<T>>> {
    return {
      "source_identifier": {},
      "source_type": {},
      "destination_identifier": {},
      "destination_type": {},
      "rule": {
        message: (model: ModelMapping, validatorKey) => {
          return `You must choose a mapping rule for Mapping ${model.sort_order + 1} - ${model.titleOrDefault}`
        },
        displayName: "Missing model mapping rule",
        afterReveal: async ($el) => {
          await window.requestAnimationFrame(async () => {
            const $container = await this.scrollToAndOpenModelMapping();
            const $button = $container.find(this.ruleButtonSelector);
            await this.registerHintToggle($container);
            $button.trigger('click');
          })
        }
      }
    }

  }

  get atleastOneLookupKeyOpts(): INavigableValidatorOptions<T> {
    return {
      validatorKey: 'atleastOneLookupKey',
      section: ValidationModalSection.Layout,
      validator: (value: never, modelMapping: ModelMapping) => {
        return modelMapping.lookup_keys.length > 0;
      },
      conditional: (model: ModelMapping, validatorKey) => {
        if (ModelMappingCantAddValues.includes(model.rule)) return false
        return Object.keys(model.model_mapping_values).length > 0 &&
          (ModelMappingUpdateRules.includes(model.rule) || (model.isObjectModelToFormPrepopulation && !model.prepopulate_from_record_id))
      },
      message: "At least one lookup key must be added.",
      displayName: "Missing lookup key",
      type: ErrorType.mustBePresent,
      afterReveal: async ($el) => {
        await window.requestAnimationFrame(async () => {
          const $container = await this.scrollToAndOpenModelMapping();
          await this.registerHintToggle($container)
        })
      }
    }
  }

  get atLeastOneMappingValueOpts(): INavigableValidatorOptions<T> {
    const model = this.model;
    return {
      validatorKey: 'atleastOneMappingValue',
      section: ValidationModalSection.Layout,
      conditional(modelMapping: ModelMapping, validatorKey: string) {
        return !!modelMapping.rule && Object.values(modelMapping.model_mapping_values).length === 0 && !ModelMappingCantAddValues.includes(modelMapping.rule)
      },
      validator(value: never, modelMapping: ModelMapping): boolean {
        return Object.values(modelMapping.model_mapping_values).length > 0
      },
      message: "At least one Mapping Value must be added",
      displayName: "Missing mapping values",
      type: ErrorType.mustBePresent,
      afterReveal: async () => {
        await window.requestAnimationFrame(async () => {
          const $button = $(ModelMappingDefinition.documentActionsViewId(model));
          const $modelMappingContainer = await this.scrollToAndOpenModelMapping();
          await this.registerButton($button, $modelMappingContainer);
        })
      }
    }
  }

  get hasValidRelation(): INavigableValidatorOptions<T> {
    const model = this.model;
    return {
      validatorKey: 'hasValidRelation',
      section: ValidationModalSection.Layout,
      conditional(modelMapping: ModelMapping, validatorKey: string) {
        return ModelMappingCantAddValues.includes(modelMapping.rule) &&
          modelMapping.sourceOrigin instanceof ModelMappingOriginLookupField &&
          modelMapping.destinationOrigin instanceof ModelMappingOriginRelationIdentifier;
      },
      validator(value: never, modelMapping: ModelMapping): boolean {
        let lookupOm = null;
        let destinationOmId = null;

        if (modelMapping.sourceOrigin instanceof ModelMappingOriginLookupField) {
          lookupOm = modelMapping.sourceOrigin.origin.object_model
        }

        let relation
        if (modelMapping.destinationOrigin instanceof ModelMappingOriginRelationIdentifier) {
          relation = modelMapping.destinationOrigin.origin.relation
          destinationOmId = modelMapping.destinationOrigin.model.id
        }

        return (relation.object_model_id === lookupOm.id || relation.related_object_models.map(x => x.id).includes(lookupOm.id));
      },
      message: (modelMapping: ModelMapping, validatorKey) => {
        return `The mapping "${modelMapping.sourceOrigin.title} to ${modelMapping.destinationOrigin.title}" is invalid. The object models do not match from source to destination.`
      },
      displayName: (validatorKey, modelMapping: ModelMapping) => {
        return `The mapping "${modelMapping.sourceOrigin.title} to ${modelMapping.destinationOrigin.title}" is invalid. The object models do not match from source to destination.`
      },
      type: ErrorType.invalidModelMapping,
      afterReveal: async () => {
        await window.requestAnimationFrame(async () => {
          const $container = await this.scrollToAndOpenModelMapping();
          const $button = $container.find(this.ruleButtonSelector);
          await this.registerHintToggle($container);
          $button.trigger('click');
        })
      }
    }
  }

  async scrollToAndOpenModelMapping() {
    return ModelMappingValidation.scrollToAndOpenModelMapping($(ModelMappingDefinition.ruleViewId(this.model)))
  }

  static async scrollToAndOpenModelMapping($from: JQuery): Promise<JQuery> {
    const $closestBox = $from.closest(".em-child.model-mapping")
    const $accordionBoxes = $from.parents(".em-child.model-mapping");
    const openBox = async (_index, box) => {
      const $accordionBox = $(box)
      if (!$accordionBox.find(".control-group > div.collapse").hasClass("show")) {
        await $accordionBox.find(".toggle-group").trigger('click')
      }
    }
    // open all model mappings needed to dig down to the current one
    await $.when.apply($, $accordionBoxes.map(openBox))
    await scrollToElement({
      $el: $closestBox,
      $container: $("div.scroll-area.work-area")
    })
    return $closestBox
  }

  highlightHint($accordionBox, targetState: { add: string, remove: string }) {
    const $alert = $accordionBox.find(`div.alert`);
    if ($alert.hasClass(targetState.remove)) {
      $alert.addClass(targetState.add);
      $alert.removeClass(targetState.remove);
    }
  }

  async registerHintToggle($container) {
    this.highlightHint($container, {add: "danger", remove: "neutral"})
    $container.on('click.validation_blur', () => {
      this.highlightHint($container, {remove: "danger", add: "neutral"})
      $container.off('click.validation_blur');
    })
  }

  async registerButton($button, $container) {
    const $dropdownCollection = $button.closest(".dropdown.inline");
    this.highlightHint($container, {add: "danger", remove: "neutral"});
    $dropdownCollection.wrap(() => CommonComponents.FieldWithErrors)
    $button.trigger('focus');

    // button gets into state where opening the dropdown menu and then selecting
    // an item requires an additional click something about the pseudo state focus
    // is causing this behavior I think. For now hack it.
    $button.on('click.first_click_after_validation_nav', () => {
      $button.trigger("blur");
      $button.off('click.first_click_after_validation_nav');
    })
    $button.on('blur.field_with_errors', (e) => {
      $button.off('blur.field_with_errors');
      $dropdownCollection.unwrap("div.field_with_errors");
      this.highlightHint($container, {add: "neutral", remove: "danger"});
    })
  }

  async wrapAndRegisterErrorsButton($button) {
    $button.wrap(() => CommonComponents.FieldWithErrors);
    $button.on('click.first_click_after_validation_nav', () => {
      $button.trigger("blur");
      $button.off('click.first_click_after_validation_nav');
    })
    $button.on('blur.field_with_errors', (e) => {
      $button.off('blur.field_with_errors');
      $button.unwrap("div.field_with_errors");
    })
  }

  private get ruleButtonSelector(): string {
    return `button${ModelMappingDefinition.ruleViewId(this.model)}`
  }
}